import { arrayRemove, arrayUnion } from "firebase/firestore"
import { orderBy } from "lodash"
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react"

import { helpRequestAtoms } from "#atoms"
import AmplitudeAnalytics from "#classes/AmplitudeAnalytics"
import Firebase from "#classes/Firebase"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { useUserContext } from "#contexts/UserContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  HELP_REQUEST_PRIORITY,
  HELP_REQUEST_STATUS,
  HelpRequestConstructor,
  HelpRequestPriority,
  HelpRequestRoom,
} from "#customTypes/composedTypes/helpRequestTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useAllDocs } from "#hooks/jotai/useSubscription"

interface HelpRequestContextType {
  activeRequests: HelpRequestConstructor[]
  sorted: HelpRequestConstructor[]
  unackedCount: number
  createHelpRequest: (
    room: HelpRequestRoom,
    priority: HelpRequestPriority,
    comment?: string
  ) => Promise<Uuid | null>
  acknowledgeRequest: (
    requestId: string,
    currentlyAcked: boolean
  ) => Promise<void>
  resolveRequest: (requestId: string) => Promise<void>
}

const HelpRequestContext = createContext<HelpRequestContextType | null>(null)

const HelpRequestContextProvider = ({ children }: { children: ReactNode }) => {
  const { effectiveUserId } = useUserContext()

  const allRequests = useAllDocs<HelpRequestConstructor>(
    helpRequestAtoms,
    DOC_TYPES.HELP_REQUESTS
  )

  const activeRequests = useMemo(
    () => allRequests.filter((r) => r.status === HELP_REQUEST_STATUS.ACTIVE),
    [allRequests]
  )

  const sorted = useMemo(
    () =>
      orderBy(
        activeRequests,
        [(r) => r.priority === HELP_REQUEST_PRIORITY.URGENT, "createdAt"],
        ["desc", "desc"]
      ),
    [activeRequests]
  )

  const unackedCount = useMemo(
    () =>
      activeRequests.filter((r) => (r.acknowledgedByIds ?? []).length === 0)
        .length,
    [activeRequests]
  )

  const createHelpRequest = useCallback(
    async (
      room: HelpRequestRoom,
      priority: HelpRequestPriority,
      comment?: string
    ) => {
      if (!effectiveUserId) return null
      const firebase = Firebase.getInstance()
      const now = new Date().toISOString()
      const helpRequestId = await firebase.addDocument<HelpRequestConstructor>(
        DOC_TYPES.HELP_REQUESTS,
        {
          room,
          priority,
          comment: comment || undefined,
          status: HELP_REQUEST_STATUS.ACTIVE,
          acknowledgedByIds: [],
          createdBy: effectiveUserId,
          createdAt: now,
          lastUpdatedBy: effectiveUserId,
          lastUpdatedAt: now,
        }
      )

      AmplitudeAnalytics.trackHelpRequestAction(
        ANALYTICS_EVENTS.HELP_REQUEST_CREATED,
        { helpRequestId, room, priority, comment }
      )

      return helpRequestId
    },
    [effectiveUserId]
  )

  const acknowledgeRequest = useCallback(
    async (requestId: string, currentlyAcked: boolean) => {
      if (!effectiveUserId) return
      const firebase = Firebase.getInstance()
      await firebase.updateDocument(DOC_TYPES.HELP_REQUESTS, requestId, {
        acknowledgedByIds: currentlyAcked
          ? arrayRemove(effectiveUserId)
          : arrayUnion(effectiveUserId),
      })

      if (!currentlyAcked) {
        const request = activeRequests.find((r) => r.id === requestId)
        const timeToAckMs = request
          ? Date.now() - new Date(request.createdAt).getTime()
          : undefined

        AmplitudeAnalytics.trackHelpRequestAction(
          ANALYTICS_EVENTS.HELP_REQUEST_ACKNOWLEDGED,
          { helpRequestId: requestId, timeToAckMs }
        )
      }
    },
    [effectiveUserId, activeRequests]
  )

  const resolveRequest = useCallback(
    async (requestId: string) => {
      const request = activeRequests.find((r) => r.id === requestId)
      const timeToResolveMs = request
        ? Date.now() - new Date(request.createdAt).getTime()
        : undefined

      const firebase = Firebase.getInstance()
      await firebase.updateDocument(DOC_TYPES.HELP_REQUESTS, requestId, {
        status: HELP_REQUEST_STATUS.RESOLVED,
      })

      AmplitudeAnalytics.trackHelpRequestAction(
        ANALYTICS_EVENTS.HELP_REQUEST_RESOLVED,
        { helpRequestId: requestId, timeToResolveMs }
      )
    },
    [activeRequests]
  )

  const value = useMemo(
    () => ({
      activeRequests,
      sorted,
      unackedCount,
      createHelpRequest,
      acknowledgeRequest,
      resolveRequest,
    }),
    [
      activeRequests,
      sorted,
      unackedCount,
      createHelpRequest,
      acknowledgeRequest,
      resolveRequest,
    ]
  )

  return (
    <HelpRequestContext.Provider value={value}>
      {children}
    </HelpRequestContext.Provider>
  )
}

export const useHelpRequestContext = () => {
  const context = useContext(HelpRequestContext)
  if (!context) {
    throw new Error(
      "useHelpRequestContext must be used within a HelpRequestContextProvider"
    )
  }
  return context
}

export default HelpRequestContextProvider
